import {
  AgendaCancelarRequest,
  AgendaDesvincularContaRequest,
  AgendaGradeLinha,
  AgendaTrocarPacienteRequest,
} from '../../../interfaces/agenda';
import { ehCancelado, ehMarcado, temPaciente } from './agenda-grade/agenda-grade';

/** Ações sobre um agendamento: as do menu ⋮ e da barra (fora novo/encaixe/reserva). */
export type AgendaAcaoAgendamento = 'alterar' | 'trocarPaciente' | 'transferir' | 'desvincular' | 'cancelar';

/** Transferir e cancelar só valem para Normal e Confirmada (ALLOWED e ALLOWED_CANCEL do original). */
export const STATUS_MOVIVEL = ['N', 'CN'];
/** Espelho de STATUS_AGENDA_EM_ANDAMENTO do back. */
export const STATUS_EM_ANDAMENTO = ['O', 'EE', 'IN', 'AD', 'E', 'FI', 'CR'];

export const MOTIVOS_ACAO = {
  semPaciente: 'Sem paciente neste horário',
  cancelado: 'Agendamento cancelado',
  soNormalConfirmada: 'Só status Normal ou Confirmada',
  semAtendimento: 'Sem atendimento vinculado',
  nenhumSelecionado: 'Selecione agendamentos na grade',
  exatamenteUm: 'Selecione exatamente 1 agendamento',
  nenhumMovivel: 'Nenhum selecionado com status Normal ou Confirmada',
  tiposMisturados: 'Selecione só consulta ou só exame',
} as const;

/** Tipo da tabela de origem: odonto mora em AGENDA_CONSULTA. */
export function tipoAgendamento(l: AgendaGradeLinha): 'CONSULTA' | 'EXAME' {
  return l.tipo === 'EXAME' ? 'EXAME' : 'CONSULTA';
}

/** Por que a ação não vale para esta linha; null = pode. */
export function motivoAcaoLinha(acao: AgendaAcaoAgendamento, l: AgendaGradeLinha): string | null {
  if (!temPaciente(l)) return MOTIVOS_ACAO.semPaciente;
  if (ehCancelado(l)) return MOTIVOS_ACAO.cancelado;
  const st = l.ieStatusAgenda ?? '';
  switch (acao) {
    case 'alterar':
      return null;
    // Liberado a todos e em qualquer status, como no original: a rota nunca travou, e quem
    // descobre o paciente errado é o balcão, muitas vezes com o atendimento já em curso.
    case 'trocarPaciente':
      return null;
    case 'transferir':
    case 'cancelar':
      return STATUS_MOVIVEL.includes(st) ? null : MOTIVOS_ACAO.soNormalConfirmada;
    case 'desvincular':
      return l.paciente?.nrAtendimento ? null : MOTIVOS_ACAO.semAtendimento;
  }
}

/**
 * Por que a ação não vale para a seleção da grade; null = pode. Transferir e cancelar aceitam
 * seleção mista (o modal lista quem fica de fora), desde que sobre ao menos um; trocar paciente e
 * desvincular são de um agendamento só.
 */
export function motivoAcaoSelecao(acao: AgendaAcaoAgendamento, linhas: AgendaGradeLinha[]): string | null {
  const marcadas = linhas.filter(ehMarcado);
  if (!marcadas.length) return MOTIVOS_ACAO.nenhumSelecionado;
  if (acao === 'trocarPaciente' || acao === 'desvincular') {
    return marcadas.length === 1 ? motivoAcaoLinha(acao, marcadas[0]) : MOTIVOS_ACAO.exatamenteUm;
  }
  if (acao === 'alterar') return null;
  const aptas = marcadas.filter(l => motivoAcaoLinha(acao, l) === null);
  if (!aptas.length) return marcadas.length === 1 ? motivoAcaoLinha(acao, marcadas[0]) : MOTIVOS_ACAO.nenhumMovivel;
  if (acao === 'transferir' && new Set(aptas.map(tipoAgendamento)).size > 1) return MOTIVOS_ACAO.tiposMisturados;
  return null;
}

export interface AgendaIgnorado {
  linha: AgendaGradeLinha;
  motivo: string;
}

export interface AgendaElegibilidadeTransferencia {
  /** Em ordem de data e hora: é a ordem em que o lote ocupa os livres do destino. */
  elegiveis: AgendaGradeLinha[];
  ignorados: AgendaIgnorado[];
  tipo: 'CONSULTA' | 'EXAME' | null;
  /** Impede abrir a transferência (nada elegível ou consulta junto com exame). */
  erro: string | null;
}

export function chaveHorario(l: AgendaGradeLinha): string {
  return `${(l.dtAgenda ?? '').slice(0, 10)} ${l.hrInicio ?? ''}`;
}

export function elegibilidadeTransferencia(linhas: AgendaGradeLinha[]): AgendaElegibilidadeTransferencia {
  const elegiveis: AgendaGradeLinha[] = [];
  const ignorados: AgendaIgnorado[] = [];
  for (const l of linhas) {
    const motivo = motivoAcaoLinha('transferir', l);
    if (motivo) ignorados.push({ linha: l, motivo });
    else elegiveis.push(l);
  }
  elegiveis.sort((a, b) => chaveHorario(a).localeCompare(chaveHorario(b)) || a.nrSequencia - b.nrSequencia);
  const tipos = new Set(elegiveis.map(tipoAgendamento));
  const tipo = tipos.size === 1 ? [...tipos][0] : null;
  let erro: string | null = null;
  if (!elegiveis.length) erro = 'Nenhum agendamento válido para transferir: só status Normal ou Confirmada, com paciente.';
  else if (tipos.size > 1) erro = 'Não é permitido transferir consulta e exame ao mesmo tempo. Selecione apenas um tipo.';
  return { elegiveis, ignorados, tipo, erro };
}

// --- Cancelar, trocar paciente e desvincular ---------------------------------------------------

/** Cancelar aceita consulta e exame juntos: cada linha vai numa chamada própria. */
export function elegibilidadeCancelamento(linhas: AgendaGradeLinha[]): { aptas: AgendaGradeLinha[]; ignorados: AgendaIgnorado[] } {
  const aptas: AgendaGradeLinha[] = [];
  const ignorados: AgendaIgnorado[] = [];
  for (const l of linhas) {
    const motivo = motivoAcaoLinha('cancelar', l);
    if (motivo) ignorados.push({ linha: l, motivo });
    else aptas.push(l);
  }
  aptas.sort((a, b) => chaveHorario(a).localeCompare(chaveHorario(b)) || a.nrSequencia - b.nrSequencia);
  return { aptas, ignorados };
}

export function corpoCancelar(l: AgendaGradeLinha, motivo: string): AgendaCancelarRequest {
  return { tipo: tipoAgendamento(l), motivo: motivo.trim() };
}

export function corpoTrocarPaciente(l: AgendaGradeLinha, cdPessoaFisica: string): AgendaTrocarPacienteRequest {
  return { tipo: tipoAgendamento(l), cdPessoaFisica: String(cdPessoaFisica).trim() };
}

export function corpoDesvincular(l: AgendaGradeLinha): AgendaDesvincularContaRequest {
  return { tipo: tipoAgendamento(l) };
}

/** Escolher o mesmo titular não troca nada: o botão fica travado. */
export function mesmoPaciente(l: AgendaGradeLinha, cdPessoaFisica: string | null | undefined): boolean {
  return !!cdPessoaFisica && String(cdPessoaFisica).trim() === String(l.paciente?.cdPessoaFisica ?? '').trim();
}
